import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable } from 'rxjs';

// We will follow the linux concept for init
// by defining steps of initialization
// every one can be subscribe to the init
// we have function allowing us to re-run init at a certain step

interface IInitStep {
  init: number | null;
  stepDone: boolean;
  initDone: boolean;
}

interface IStepHandler {
  instance: Object;
  initializer: Function;
  context: any;
  initFuncName: string;
}

interface IStepsHandler {
  [key: string]: IStepHandler[];
  init0: IStepHandler[];
  init1: IStepHandler[];
  init2: IStepHandler[];
  init3: IStepHandler[];
  init4: IStepHandler[];
  init5: IStepHandler[];
}

export interface Initializable {
  init(): Promise<any>;
}

@Injectable({
  providedIn: 'root',
})
export class AppInitService {
  private stepsHandler: IStepsHandler = {
    init0: [],
    init1: [],
    init2: [],
    init3: [],
    init4: [],
    init5: [],
  };
  private stepObservable: BehaviorSubject<IInitStep>;

  firstInitDone = false;
  done = false;
  isLightInit = false;
  pendingUrl: string | null = null;

  constructor() {
    this.stepObservable = new BehaviorSubject<IInitStep>({
      init: null,
      stepDone: false,
      initDone: false,
    });
  }

  register(step: string, instance: Initializable) {
    let stepHandlers = this.stepsHandler[step];
    stepHandlers.push({
      instance: instance,
      initializer: instance.init,
      context: {},
      initFuncName: 'init',
    });
  }

  // Just use to launch the init
  async run() {
    return this.init0();
  }

  runLightInit(): Promise<any> {
    this.done = false;
    this.isLightInit = true;
    return this.init2();
  }

  initFrom1() {
    return this.init1();
  }

  initFrom2() {
    return this.init2();
  }

  initFrom3() {
    return this.init3();
  }

  initFrom4() {
    return this.init4();
  }

  initFrom5() {
    return this.init5();
  }

  onStepChange(): Observable<IInitStep> {
    return this.stepObservable;
  }

  async init0(): Promise<any> {
    this.done = false;
    await this.runStep({ init: 0, stepDone: false, initDone: false });
    return this.init1();
  }

  async init1(): Promise<any> {
    await this.runStep({ init: 1, stepDone: false, initDone: false });
    return this.init2();
  }

  async init2(): Promise<any> {
    await this.runStep({ init: 2, stepDone: false, initDone: false });
    return this.init3();
  }

  async init3(): Promise<any> {
    if (this.isLightInit) {
      await this.runStep({ init: 3, stepDone: false, initDone: true });
      this.isLightInit = false;
      this.done = true;
    } else {
      await this.runStep({ init: 3, stepDone: false, initDone: false });
      return this.init4();
    }
  }

  async init4(): Promise<any> {
    await this.runStep({ init: 4, stepDone: false, initDone: false });
    return this.init5();
  }

  async init5(): Promise<any> {
    await this.runStep({ init: 5, stepDone: false, initDone: false });
    this.stepObservable.next({ init: 5, stepDone: true, initDone: true });
    this.done = true;
    this.firstInitDone = true;
  }

  async runStep(state: IInitStep): Promise<void> {
    this.stepObservable.next(state);
    const handlers = this.stepsHandler['init' + state.init];
    if (handlers.length > 0) {
      const promises = handlers.map((elt) =>
        (elt.instance as Record<string, () => Promise<any>>)[elt.initFuncName]().catch((err) => {
          console.error(`APP INIT step ${state.init} service failed, continuing in degraded mode:`, err);
        })
      );
      await Promise.allSettled(promises);
    }
    state.stepDone = true;
    this.stepObservable.next(state);
  }
}
